import React, { useEffect, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import productsDb from "../../../../mocks/db/products.db";
import Table, { TBody, Td, TFoot, Th, THead, Tr } from "../../../../components/ui/Table";
import Icon from "../../../../components/icon/Icon";
import { CardBody, CardHeader, CardHeaderChild, CardTitle } from "../../../../components/ui/Card";
import { TableCardFooterTemplate } from "../../../../templates/common/TableParts.template";
import TableSkeletonLoader from "../../../../components/loader/skeleton/tableSkeletonLoader";
import AddMachineModel from "../../../../components/admin/home/addMachineModel";
import Button from "../../../../components/ui/Button";
import { useNavigate } from "react-router-dom";

const defaultData = [productsDb[0], productsDb[1], productsDb[2]];

const columnHelper = createColumnHelper();

const TablePartial = ({ isLoading, userContactData, pagination, setPageSize, setPageIndex, handleResetPagination }) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const [modalStatus, setModalStatus] = useState(false);
  const [editUserContactInfo, setEditUserContactInfo] = useState({});

  useEffect(() => {
    if (!modalStatus) setEditUserContactInfo({});
  }, [modalStatus]);

  const columns = [
    columnHelper.accessor("Image", {
      cell: (info) => (
        <div className="flex items-center justify-center">
          <img
            className="aspect-[9/12] w-14 h-14 rounded object-cover 2xl:w-20 2xl:h-20"
            src={info?.row?.original?.PictureUrl}
            alt=""
          />
        </div>
      ),
      header: "Image",
      footer: "Image",
    }),
    columnHelper.accessor("FirstName", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.FirstName || "-"}</div>,
      header: "First Name",
      footer: "First Name",
    }),
    columnHelper.accessor("LastName", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.LastName || "-"}</div>,
      header: "Last Name",
      footer: "Last Name",
    }),
    columnHelper.accessor("Title", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.Title || "-"}</div>,
      header: "Title",
      footer: "Title",
    }),
    columnHelper.accessor("Status", {
      cell: (info) => (
        <div className="text-sm text-center">
          {/* API returns ContactStatusName, not Status */}
          {info?.row?.original?.ContactStatusName || info?.row?.original?.Status || "-"}
        </div>
      ),
      header: "Status",
      footer: "Status",
    }),
    columnHelper.accessor("Email", {
      cell: (info) => <div className="text-sm text-center">{info?.row?.original?.Email || "-"}</div>,
      header: "Email",
      footer: "Email",
    }),
    columnHelper.accessor("Mobile Phone", {
      cell: (info) => (
        <div className="text-sm text-center">
          {/* API returns 'Phone' as the mobile number */}
          {info?.row?.original?.Mobile || info?.row?.original?.Phone || "-"}
        </div>
      ),
      header: "Mobile Phone",
      footer: "Mobile Phone",
    }),
    columnHelper.accessor("Office Phone", {
      cell: (info) => (
        <div className="text-sm text-center">
          {/* OfficePhone is stored separately from Phone (mobile) */}
          {info?.row?.original?.OfficePhone || "-"}
        </div>
      ),
      header: "Office Phone",
      footer: "Office Phone",
    }),
    columnHelper.accessor("Active Profile", {
      cell: (info) => (
        <div className="text-sm text-center">
          {info?.row?.original?.Active ? (
            <div className="text-[#00BE5F]">Yes</div>
          ) : (
            <div className="text-[#FE2B06]">No</div>
          )}
        </div>
      ),
      header: "Active Profile",
      footer: "Active Profile",
    }),
    columnHelper.accessor("Action", {
      cell: (info) => (
        <div className="text-sm flex text-center  justify-center">
          <div
            className="text-[#0EA5E9] cursor-pointer pr-[5px]"
            onClick={
              () => {
                navigate(`/admin/edit-user-contact/${info?.row?.original.ContactId}`, {
                  state: { editUserInfo: info?.row?.original },
                });
              }}
          >
            Edit
          </div>
          <div className="text-[#FE2B06] pl-[5px]  border-l border-l-[#E6E6E6]">Delete</div>
        </div>
      ),
      header: "Action",
      footer: "Action",
      size: 100,
      minSize: 100,
      meta: {
        sticky: "right",
      },
    }),
  ];

  const handleCloseUserContactPopUp = () => {
    setModalStatus(false);
  };

  const table = useReactTable({
    data: userContactData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // debugTable: true,
  });

  return (
    <>
      <div className="flex justify-between">
        <CardHeader>
          <CardHeaderChild>
            <CardTitle> Company Contacts & Users</CardTitle>
          </CardHeaderChild>
        </CardHeader>
        <div className="m-[14px] text-end">
          <Button
            className="!bg-[#FEBA06] border-none !text-black px-[20px] py-[12px] rounded-xl font-medium"
            onClick={() => {
              navigate(`/admin/edit-user-contact`);
            }}
          >
            Add User Contacts
          </Button>
        </div>
      </div>

      <CardBody className="overflow-auto">
        <div className="w-full">
          <div className="overflow-x-auto">
            <Table className="min-w-[1200px]">
              <THead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header?.id}
                        isColumnBorder={false}
                        className={`text-left ${header.column.columnDef.meta?.sticky === 'right' ? '!bg-gray-50 dark:!bg-[#09090b] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.3)]' : ''}`}
                        style={{
                          width: header.getSize(),
                          ...(header.column.columnDef.meta?.sticky === 'right' && {
                            position: 'sticky',
                            right: 0,
                            zIndex: 10,
                          })
                        }}
                      >
                        {header?.isPlaceholder ? null : (
                          <div
                            key={header.id}
                            aria-hidden="true"
                            {...{
                              className: header.column.getCanSort()
                                ? `cursor-pointer select-none flex items-center justify-center text-center`
                                : ``,
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <Icon icon="HeroChevronUp" className="ltr:ml-1.5 rtl:mr-1.5" />,
                              desc: <Icon icon="HeroChevronDown" className="ltr:ml-1.5 rtl:mr-1.5" />,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </THead>
              <TBody>
                {!isLoading ? (
                  Number(pagination?.totalRecords) <= 0 ? (
                    <Tr>
                      <Td className="text-center py-[100px] font-normal text-[30px]" colspan="11">
                        No Record Found
                      </Td>
                    </Tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <Tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <Td
                            key={cell.id}
                            style={{
                              ...(cell.column.columnDef.meta?.sticky === 'right' && {
                                position: 'sticky',
                                right: 0,
                                zIndex: 10,
                              })
                            }}
                            className={`${cell.column.columnDef.meta?.sticky === 'right' ? 'shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.3)] !bg-white group-even/Tr:!bg-zinc-100 dark:!bg-[#18181b] dark:group-even/Tr:!bg-[#121214]' : ''}`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Td>
                        ))}
                      </Tr>
                    ))
                  )
                ) : (
                  [...Array(pagination?.showRecords)].map((_, index) => (
                    <Tr className="bg-gray-100">
                      <TableSkeletonLoader length={10} />
                    </Tr>
                  ))
                )}
              </TBody>
              <TFoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <Tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        isColumnBorder={false}
                        className={`text-left ${header.column.columnDef.meta?.sticky === 'right' ? '!bg-gray-50 dark:!bg-[#09090b] shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.3)]' : ''}`}
                        style={{
                          width: header.getSize(),
                          ...(header.column.columnDef.meta?.sticky === 'right' && {
                            position: 'sticky',
                            right: 0,
                            zIndex: 10,
                          })
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            key={header.id}
                            aria-hidden="true"
                            {...{
                              className: header.column.getCanSort()
                                ? `cursor-pointer select-none flex items-center justify-center`
                                : ``,
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <Icon icon="HeroChevronUp" className="ltr:ml-1.5 rtl:mr-1.5" />,
                              desc: <Icon icon="HeroChevronDown" className="ltr:ml-1.5 rtl:mr-1.5" />,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </TFoot>
            </Table>
          </div>
        </div>
      </CardBody>
      <TableCardFooterTemplate
        table={table}
        pagination={pagination}
        setPageSize={setPageSize}
        setPageIndex={setPageIndex}
      />

      {/* Edit Machine Model */}

      <AddMachineModel
        handleResetPagination={handleResetPagination}
        handleCloseMachinePopUp={handleCloseUserContactPopUp}
        modalStatus={modalStatus}
        setModalStatus={setModalStatus}
        editMachineInfo={editUserContactInfo}
      />
    </>
  );
};

export default TablePartial;
